import { Controller } from "@hotwired/stimulus"
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill
} from "@simplewebauthn/browser"

export default class extends Controller {
  static targets = ["registerButton", "loginButton", "statusMessage", "nicknameInput", "passkeySection"]
  static values = {
    registrationUrl: { type: String, default: "/passkeys/registrations" },
    registrationCallbackUrl: { type: String, default: "/passkeys/registrations/callback" },
    authenticationUrl: { type: String, default: "/passkeys/sessions" },
    authenticationCallbackUrl: { type: String, default: "/passkeys/sessions/callback" }
  }

  connect() {
    if (!browserSupportsWebAuthn()) {
      this.hidePasskeyUI()
      return
    }

    if (this.hasLoginButtonTarget) {
      this.initConditionalUI()
    }
  }

  hidePasskeyUI() {
    if (this.hasPasskeySectionTarget) {
      this.passkeySectionTarget.style.display = "none"
    }
  }

  async register(event) {
    event.preventDefault()
    const button = this.registerButtonTarget
    const originalText = button.textContent

    try {
      button.disabled = true
      button.textContent = "Setting up..."
      button.setAttribute("aria-busy", "true")
      this.clearStatus()

      const optionsResponse = await this.fetchJSON(this.registrationUrlValue, { method: "POST" })
      const credential = await startRegistration({ optionsJSON: optionsResponse })

      const nickname = this.hasNicknameInputTarget ? this.nicknameInputTarget.value : ""
      await this.fetchJSON(this.registrationCallbackUrlValue, {
        method: "POST",
        body: JSON.stringify({ ...credential, nickname })
      })

      this.showStatus("Passkey registered successfully!", "success")
      window.Turbo.visit(window.location.href, { action: "replace" })
    } catch (error) {
      if (error.name === "NotAllowedError") {
        this.showStatus("Registration was cancelled.", "info")
      } else {
        this.showStatus(`Registration failed: ${error.message}`, "error")
      }
    } finally {
      button.disabled = false
      button.textContent = originalText
      button.removeAttribute("aria-busy")
    }
  }

  async authenticate(event) {
    event.preventDefault()
    const button = this.loginButtonTarget
    const originalText = button.textContent

    try {
      button.disabled = true
      button.textContent = "Verifying..."
      button.setAttribute("aria-busy", "true")
      this.clearStatus()

      const optionsResponse = await this.fetchJSON(this.authenticationUrlValue, { method: "POST" })
      const assertion = await startAuthentication({ optionsJSON: optionsResponse })

      const result = await this.fetchJSON(this.authenticationCallbackUrlValue, {
        method: "POST",
        body: JSON.stringify(assertion)
      })

      window.Turbo.visit(result.redirect_to || "/", { action: "advance" })
    } catch (error) {
      if (error.name === "NotAllowedError") {
        this.showStatus("Authentication was cancelled.", "info")
      } else {
        this.showStatus(`Sign in failed: ${error.message}`, "error")
      }
    } finally {
      button.disabled = false
      button.textContent = originalText
      button.removeAttribute("aria-busy")
    }
  }

  async initConditionalUI() {
    try {
      const supported = await browserSupportsWebAuthnAutofill()
      if (!supported) return

      const optionsResponse = await this.fetchJSON(this.authenticationUrlValue, { method: "POST" })
      const assertion = await startAuthentication({ optionsJSON: optionsResponse, useBrowserAutofill: true })

      const result = await this.fetchJSON(this.authenticationCallbackUrlValue, {
        method: "POST",
        body: JSON.stringify(assertion)
      })

      window.Turbo.visit(result.redirect_to || "/", { action: "advance" })
    } catch (_error) {
      // Conditional UI errors are silently ignored
    }
  }

  async fetchJSON(url, options = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
        ...options.headers
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Request failed (${response.status})`)
    }

    return response.json()
  }

  showStatus(message, type) {
    if (!this.hasStatusMessageTarget) return

    const target = this.statusMessageTarget
    target.textContent = message
    target.setAttribute("role", "status")
    target.className = type === "success"
      ? "mt-2 text-sm text-green-600"
      : type === "error"
        ? "mt-2 text-sm text-red-600"
        : "mt-2 text-sm text-gray-600"
    target.style.display = "block"
  }

  clearStatus() {
    if (!this.hasStatusMessageTarget) return
    this.statusMessageTarget.style.display = "none"
    this.statusMessageTarget.textContent = ""
  }
}
